import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styles from "./BlogPost.module.css";
import logoImage from "../assets/images/canacon_logo2.png";

// News articles data
const newsArticles = [
  {
    id: 1,
    title: "From Scare Ads to Smart Ads: Using Data the Right Way in 2025",
    slug: "from-scare-ads-to-smart-ads-using-data-right-way-2025",
    excerpt: "Many people first learned how much the web knows about them when a VPN banner appeared this spring. The ad greeted each viewer with oddly precise facts and then warned that only a paid subscription could hide that information.",
    content: `
      <p class="lead">Many people first learned how much the web knows about them when a VPN banner appeared this spring. The ad greeted each viewer with oddly precise facts and then warned that only a paid subscription could hide that information. The shock came from seeing personal details in black and white, yet the ad simply stitched together data already moving through ordinary ad exchanges every time a page loads. One data-broker study claims that modern targeting can now reach more than ninety percent of all adults in North America, proof that the raw material for such ads is everywhere.</p>
      
      <p>To understand how this happens, picture three puzzle pieces coming together. First, the browser stores cookies that remember where you have been. Second, device-fingerprint scripts note settings like screen size and fonts, which act as a partial ID even if cookies are blocked. Third, data brokers buy or trade offline records—loyalty-card swipes, property files, even sporting-event results—which they merge with those online IDs. When an advertiser bids for an impression, the exchange can pass enough of these fragments to let the buyer decide whether the visitor matches a target audience. Investigations into military location tracking illustrate the depth of data available, showing that billions of phone pings can travel from ordinary mobile apps to resellers without the user ever noticing.</p>
      
      <p>The same data flow can be used responsibly. Marketers who rely on first-party data, meaning information users give directly—such as email sign-ups, purchase history, or stated interests—see better results without creeping people out. McDonald's recent campaign in Australia is a useful example. By hashing the email addresses of app users and creating look-alike groups, the team lifted revenue by ninety-two percent compared with segments built from third-party cookies. Independent research released last month echoed the finding: campaigns that lean on first-party data and clear consent generated stronger return on ad spend than broad retargeting. Adding structured data to product pages—prices, reviews, FAQ snippets—helps search engines surface richer results in AI overviews, putting trusted content in front of buyers who are already comparing options.</p>
      
      <p>Regulation is pushing the industry further in this direction. Google has paused its plan to remove third-party cookies from Chrome, choosing instead to leave control in the privacy settings for now, but analysts see this as a temporary truce rather than a final answer. In the European Union, Consent Mode v2 already blocks remarketing scripts unless a visitor opts in, and similar rules are on the way in Canada. Apple, for its part, strips hidden tracking codes from links shared in Mail and Messages, which breaks many legacy attribution methods. The safest response is to move measurement server-side, respect every consent signal, and ensure pages still load quickly when tracking is limited.</p>
      
      <p>Good marketing thrives on relevance, and relevance grows when people trust the way their data is handled. Ask for only the details you need, explain how they improve the experience, and store them securely. Pair those efforts with fast sites, well-labeled content, and ethical segmentation, and you can raise conversions without resorting to scare tactics. The data is out there; success comes from using it in a way that keeps both customers and regulators on your side.</p>
    `,
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
    category: 'Digital Marketing',
    date: 'June 23, 2025',
    readTime: 6,
    author: {
      name: 'Onur Gul',
      avatar: logoImage,
      bio: 'Founder & CEO of Canacon Media with expertise in digital marketing, data privacy, and ethical advertising strategies.',
      title: 'Founder & CEO'
    },
    sources: []
  },
  {
    id: 2,
    title: "Beyond the Click: Adapting SEO to AI-Driven Search in 2025",
    slug: "beyond-click-adapting-seo-ai-driven-search-2025",
    excerpt: "The past few months have felt like a plot twist for anyone who watches their analytics first thing in the morning. Google's new AI Overviews began showing full answers at the top of countless queries, cutting the need to click through to a site at all.",
    content: `
      <p class="lead">The past few months have felt like a plot twist for anyone who watches their analytics first thing in the morning. Google's new AI Overviews began showing full answers at the top of countless queries, cutting the need to click through to a site at all. Major publishers have already reported traffic dips of 20 percent or more, proof that zero-click search is no longer a fringe worry but a mainstream reality.</p>
      
      <p>For marketers, the first adjustment is to rethink what "visibility" means. Ranking at position one still matters, yet the real prize is now the snippet inside the overview box. Eye-catching, concise summaries—paired with schema that flags statistics, how-to steps, and FAQs—give Google's AI the structured hooks it prefers. Studies of 2025 search behavior show that answer engines, community platforms, and even Reddit threads are siphoning visits away from the traditional ten-blue-links model. Brands that contribute credible, first-hand insights to those spaces are meeting users before they ever reach a results page.</p>
      
      <p>A second jolt arrived with the silent June 2025 algorithm update. Sites woke up to steady rankings but falling click counts, a sign that impressions were up while user intent shifted elsewhere. Technical audits revealed that slow-loading product pages and generic blog intros were the first to be passed over. Teams that responded quickly—compressing images, rewriting ledes to answer a question in the first two lines, and trimming bloated CSS—recovered ground within days. In short, performance and clarity now share equal billing.</p>
      
      <p>Content tone is the third piece of the puzzle. Google's helpful-content refinements keep rewarding writers who demonstrate lived experience. That means quoting a client call, embedding a short case study, or linking to original data—anything that proves a real human did the work. Search analysts forecast that E-E-A-T signals and brand familiarity will outweigh raw keyword density by year's end. Pair that with diversified traffic sources—newsletters, LinkedIn posts, and short-form video—and the organic channel becomes less brittle when the next tweak rolls out.</p>
      
      <p>No crystal ball saw this exact mix of AI summaries and algorithm swings arriving together, yet the playbook remains clear: write for people first, mark up pages so machines understand them, and measure success beyond a single metric. If your content can earn a mention inside an AI Overview, spark discussion in a niche forum, and still load in under two seconds, you will stand out in the cluttered search landscape of 2025.</p>
    `,
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop",
    category: 'Technology',
    date: 'June 20, 2025',
    readTime: 5,
    author: {
      name: 'Onur Gul',
      avatar: logoImage,
      bio: 'Founder & CEO of Canacon Media with expertise in digital marketing, SEO strategy, and emerging technology trends.',
      title: 'Founder & CEO'
    },
    sources: []
  }
];

const NewsPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Find the news article with matching slug
  const article = newsArticles.find(article => article.slug === slug);
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // If article not found, redirect to news page
    if (!article && slug) {
      navigate('/news');
    }
  }, [article, slug, navigate]);
  
  if (!article) {
    return <div className={styles.loading}>Loading...</div>;
  }
  
  return (
    <div className={styles.newsPost}>
      {/* Navigation */}
      <div className={styles.navigation}>
        <div className={styles.container}>
          <Link to="/news" className={styles.backLink}>
            ← Back to News
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article className={styles.articleWrapper}>
        <div className={styles.container}>
          <header className={styles.articleHeader}>
            <div className={styles.categoryBadge}>
              <span className={styles.category}>{article.category}</span>
            </div>
            
            <h1 className={styles.articleTitle}>{article.title}</h1>
            
            <div className={styles.articleMeta}>
              <div className={styles.authorSection}>
                <img 
                  src={article.author.avatar} 
                  alt={article.author.name} 
                  className={styles.authorAvatar}
                />
                <div className={styles.authorInfo}>
                  <span className={styles.authorName}>By {article.author.name}</span>
                  <span className={styles.authorTitle}>{article.author.title}</span>
                </div>
              </div>
              
              <div className={styles.publishInfo}>
                <time className={styles.publishDate}>{article.date}</time>
                <span className={styles.readTime}>{article.readTime} min read</span>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className={styles.featuredImage}>
            <img src={article.image} alt={article.title} />
            <div className={styles.imageCaption}>
              {article.category === 'Digital Marketing' 
                ? 'Modern data collection and advertising practices require ethical approaches that respect user privacy while maintaining effectiveness.'
                : 'AI-driven search is transforming how content appears in search results, requiring new SEO strategies for 2025.'
              }
            </div>
          </div>

          {/* Article Content */}
          <div className={styles.articleContent}>
            <div 
              className={styles.contentBody}
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            
            {/* Sources */}
            {article.sources.length > 0 && (
              <div className={styles.sources}>
                <h4 className={styles.sourcesTitle}>Sources</h4>
                <ul className={styles.sourcesList}>
                  {article.sources.map((source, index) => (
                    <li key={index} className={styles.sourceItem}>{source}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Article Footer */}
          <footer className={styles.articleFooter}>
            <div className={styles.authorBio}>
              <img 
                src={article.author.avatar} 
                alt={article.author.name} 
                className={styles.authorAvatarLarge}
              />
              <div className={styles.bioContent}>
                <h3 className={styles.authorNameLarge}>{article.author.name}</h3>
                <p className={styles.authorBioText}>{article.author.bio}</p>
              </div>
            </div>
            
            <div className={styles.shareSection}>
              <h4 className={styles.shareTitle}>Share this article</h4>
              <div className={styles.shareButtons}>
                <a 
                  href={`https://twitter.com/intent/tweet?url=https://canacon.com/news/${article.slug}&text=${encodeURIComponent(article.title)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={styles.shareButton}
                  aria-label="Share on X"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  
                </a>
                <a 
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=https://canacon.com/news/${article.slug}&title=${encodeURIComponent(article.title)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={styles.shareButton}
                  aria-label="Share on LinkedIn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
                    <circle cx="4" cy="4" r="2"/>
                  </svg>
                  
                </a>
                <a 
                  href={`mailto:?subject=${encodeURIComponent(article.title)}&body=Check out this article: https://canacon.com/news/${article.slug}`} 
                  className={styles.shareButton}
                  aria-label="Share via Email"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  
                </a>
              </div>
            </div>
          </footer>

          {/* Related Articles */}
          <section className={styles.relatedSection}>
            <h3 className={styles.relatedTitle}>Related News</h3>
            <div className={styles.relatedGrid}>
              <div className={styles.relatedCard}>
                <div className={styles.relatedContent}>
                  <span className={styles.relatedCategory}>Technology</span>
                  <h4 className={styles.relatedArticleTitle}>More insights coming soon</h4>
                  <p className={styles.relatedDate}>Stay tuned for more expert analysis</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </article>
    </div>
  );
};

export default NewsPost; 